import { SortDirectionEnum, useCustomQuery } from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useExportHook, useExportProps, useExportResult } from './types';
import { getDateFilter } from './dateFilterUtils';

export const useAccountsExport: useExportHook = (
  props: useExportProps
): useExportResult => {
  const [rows, setRows] = useState([]);

  const headers = [
    { label: 'ID', key: 'id' },
    { label: 'First Name', key: 'firstName' },
    { label: 'Last Name', key: 'lastName' },
    { label: 'Email', key: 'email' },
    { label: 'Phone', key: 'phone' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Created Time', key: 'createdTime' },
    { label: 'Roles', key: 'roles' },
    { label: 'Groups', key: 'groups' },
  ];

  const { data: accounts, isLoading } = useCustomQuery({
    query: LIST_ACCOUNTS,
    cacheKey: props.workspace
      ? ['exportWorkspaceAccounts', props.workspace.id, props.dateFilter]
      : ['exportAccounts', props.dateFilter],
    variables: {
      filter: props.workspace
        ? {
            workspace: { eq: props.workspace.id },
            ...(Object.keys(getDateFilter(props.dateFilter)).length > 0 && {
              createdAt: {
                ge: getDateFilter(props.dateFilter).startDate,
                le: getDateFilter(props.dateFilter).endDate,
              },
            }),
          }
        : {
            ...(Object.keys(getDateFilter(props.dateFilter)).length > 0 && {
              createdAt: {
                ge: getDateFilter(props.dateFilter).startDate,
                le: getDateFilter(props.dateFilter).endDate,
              },
            }),
          },
      orderBy: { field: 'createdAt', order: SortDirectionEnum.Asc },
    },
    options: {
      enabled: props.download,
      gcTime: 10000,
      staleTime: 10000,
    },
  });

  useEffect(() => {
    if (!accounts) return;

    const exportRows = accounts.findAccounts.items.map((item: any) => {
      const createdAt = new Date(item.createdAt);
      return {
        id: item.id,
        firstName: item.name?.first,
        lastName: item.name?.last,
        email: item.email,
        phone: `${item.phone?.callingCode}${item.phone?.phoneNumber}` || '',
        createdDate: createdAt.toISOString().split('T')[0], // YYYY-MM-DD
        createdTime: createdAt.toISOString().split('T')[1].substring(0, 5), // HH:MM
        roles: item.roles?.map((r: any) => r.name).join(', '),
        groups: item.groups?.map((g: any) => g.name).join(', '),
      };
    });

    setRows(exportRows);
    props.onDataReady();
  }, [accounts]);

  return {
    headers,
    rows,
    isLoading,
  };
};

const LIST_ACCOUNTS = `
  query findAccounts($filter: AccountFilter, $orderBy: OrderByInput) {
    findAccounts(filter: $filter, orderBy: $orderBy) {
      items {
        id
        name {
          first
          last
        }
        email
        phone {
          callingCode
          phoneNumber
        }
        createdAt
        roles {
          id
          name
        }
        groups {
          name
        }
      }
    }
  }
`;
